import React, { useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Popover, Tooltip } from "antd"
import { ILeaveCalendarDay, ILeaveCalendarEntry } from "services/leaveService"

const ISO = "YYYY-MM-DD"

/** Approved leave is settled, pending is not yet, planned is only an intention. */
const KIND_STYLE: Record<string, { chip: string; dot: string; label: string }> = {
  approved: {
    chip: "bg-[#E7F1EE] text-[#1F5C4D] border-[#BCD9D1]",
    dot: "bg-[#2C6B5E]",
    label: "Approved",
  },
  pending: {
    chip: "bg-[#FBF1DD] text-[#7A5710] border-[#EDD8A9]",
    dot: "bg-[#B8860B]",
    label: "Awaiting a decision",
  },
  planned: {
    chip: "bg-[#EDEFF4] text-[#4A5568] border-[#D5DAE3] border-dashed",
    dot: "bg-[#8A94A6]",
    label: "Planned, not requested",
  },
}

interface LeaveMonthGridProps {
  /** Any date inside the month to render. */
  month: Dayjs
  entries: ILeaveCalendarEntry[]
  days: ILeaveCalendarDay[]
  loading?: boolean
  /** Names are hidden from the chip when the viewer may not see leave types (LC-04). */
  showLeaveType?: boolean
  emptyMessage?: string
  /** Three-up in a quarter: shorter cells, two names deep, no repeated legend. */
  compact?: boolean
  /**
   * TO-06. Days where more people plan to be away than the department allows at once, as
   * yyyy-MM-dd. The roster's whole purpose is spotting these, so they have to survive the move
   * from the timeline to the grid — a calendar that hides the clash would be prettier and
   * useless.
   */
  flaggedDays?: Record<string, number>
  /**
   * Opens whatever the chip stands for. The planning roster passes it so a supervisor can act
   * on a planned period from the calendar views, not only from the timeline.
   */
  onEntryClick?: (entry: ILeaveCalendarEntry) => void
}

/**
 * A month of leave as a calendar, not a spreadsheet.
 *
 * The timeline view answers "who overlaps with whom across a quarter", which is the question a
 * supervisor coordinating cover has. This answers the one everybody else has — "what does
 * next month look like" — and a person scanning for that reads a month grid far faster than a
 * horizontally scrolling matrix of 30 narrow columns.
 *
 * Three cells deep per day, then "+N more" behind a click. Showing every name would make row
 * heights jump around and turn a busy week into a wall; three is enough to see whether a day is
 * quiet or crowded, which is what the glance is for.
 */
export const LeaveMonthGrid: React.FC<LeaveMonthGridProps> = ({
  month,
  entries,
  days,
  loading = false,
  showLeaveType = true,
  emptyMessage = "Nobody is away this month.",
  compact = false,
  flaggedDays,
  onEntryClick,
}) => {
  const [hoveredPerson, setHoveredPerson] = useState<number | null>(null)

  const dayMeta = useMemo(() => {
    const map = new Map<string, ILeaveCalendarDay>()
    days.forEach((day) => {
      if (day.date) map.set(dayjs(day.date, "DD/MM/YYYY").format(ISO), day)
    })
    return map
  }, [days])

  // Six rows of seven always: a month that starts on a Sunday and runs 31 days needs the sixth,
  // and a grid that changes height as you page through the year is unpleasant to use.
  const gridStart = month.startOf("month").startOf("week")
  const cells = useMemo(
    () => Array.from({ length: 42 }, (_, index) => gridStart.add(index, "day")),
    [gridStart]
  )

  const byDay = useMemo(() => {
    const map = new Map<string, ILeaveCalendarEntry[]>()

    entries.forEach((entry) => {
      if (!entry.first_day || !entry.last_day) return

      let cursor = dayjs(entry.first_day, "DD/MM/YYYY")
      const last = dayjs(entry.last_day, "DD/MM/YYYY")

      while (cursor.isSame(last) || cursor.isBefore(last)) {
        const key = cursor.format(ISO)
        map.set(key, [...(map.get(key) ?? []), entry])
        cursor = cursor.add(1, "day")
      }
    })

    return map
  }, [entries])

  const today = dayjs().format(ISO)
  const monthIndex = month.month()

  if (loading) {
    return (
      <div className="grid grid-cols-7 gap-px bg-neutral40 border border-neutral40 rounded-lg overflow-hidden">
        {Array.from({ length: 42 }, (_, index) => (
          <div
            key={index}
            className={`bg-white animate-pulse ${compact ? "h-[70px]" : "h-[104px]"}`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-7 gap-px">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
          <div
            key={label}
            className="font-avenir text-[11px] uppercase tracking-wider text-padeSubtext text-center pb-1"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-neutral40 border border-neutral40 rounded-lg overflow-hidden">
        {cells.map((day) => {
          const key = day.format(ISO)
          const meta = dayMeta.get(key)
          const dayEntries = byDay.get(key) ?? []
          const outside = day.month() !== monthIndex
          const isToday = key === today
          const nonWorking = meta?.is_non_working
          const overThreshold = flaggedDays?.[key]

          const visible = dayEntries.slice(0, compact ? 2 : 3)
          const overflow = dayEntries.length - visible.length

          return (
            <div
              key={key}
              className={`${compact ? "min-h-[70px] p-1" : "min-h-[104px] p-1.5"} flex flex-col gap-1 ${
                outside
                  ? "bg-[#FBFBFC]"
                  : overThreshold
                    ? "bg-[#FDECEA]"
                    : nonWorking
                      ? "bg-[#F7F8FA]"
                      : "bg-white"
              }`}
            >
              <div className="flex items-baseline justify-between gap-1">
                <span
                  className={`font-avenir text-xs tabular-nums w-5 h-5 flex items-center justify-center rounded-full ${
                    isToday
                      ? "bg-primary500 text-white font-semibold"
                      : outside
                        ? "text-neutral70"
                        : "text-padeBlack"
                  }`}
                >
                  {day.date()}
                </span>

                {overThreshold ? (
                  <Tooltip
                    title={`${overThreshold} people plan to be away — more than this department allows at once.`}
                  >
                    <span className="font-avenir text-[10px] text-[#B42318] font-semibold tabular-nums">
                      {overThreshold} away
                    </span>
                  </Tooltip>
                ) : meta?.holiday ? (
                  <Tooltip title={meta.holiday}>
                    <span className="font-avenir text-[10px] text-[#8A6412] truncate max-w-[70px]">
                      {meta.holiday}
                    </span>
                  </Tooltip>
                ) : dayEntries.length > 0 && !outside ? (
                  <span className="font-avenir text-[10px] text-padeSubtext tabular-nums">
                    {dayEntries.length}
                  </span>
                ) : null}
              </div>

              {visible.map((entry, index) => (
                <PersonChip
                  key={`${entry.employee_request_id}-${index}`}
                  entry={entry}
                  onSelect={
                    onEntryClick ? () => onEntryClick(entry) : undefined
                  }
                  showLeaveType={showLeaveType}
                  dimmed={
                    hoveredPerson !== null && hoveredPerson !== entry.person_id
                  }
                  onHover={() => setHoveredPerson(entry.person_id)}
                  onLeave={() => setHoveredPerson(null)}
                />
              ))}

              {overflow > 0 ? (
                <Popover
                  trigger="click"
                  placement="right"
                  title={
                    <span className="font-avenir text-sm">
                      {day.format("ddd D MMM")} · {dayEntries.length} away
                    </span>
                  }
                  content={
                    <div className="flex flex-col gap-1 max-h-[260px] overflow-y-auto min-w-[190px]">
                      {dayEntries.map((entry, index) => (
                        <PersonChip
                          key={`${entry.employee_request_id}-all-${index}`}
                          entry={entry}
                          onSelect={
                            onEntryClick ? () => onEntryClick(entry) : undefined
                          }
                          showLeaveType={showLeaveType}
                        />
                      ))}
                    </div>
                  }
                >
                  <button className="font-avenir text-[11px] text-primary500 text-left hover:underline">
                    +{overflow} more
                  </button>
                </Popover>
              ) : null}
            </div>
          )
        })}
      </div>

      {compact ? null : entries.length === 0 ? (
        <p className="font-avenir text-sm text-padeSubtext text-center m-0 py-2">
          {emptyMessage}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          {/* Only the kinds actually present: a legend explaining states nobody can see on the
              screen is noise. */}
          {Object.entries(KIND_STYLE)
            .filter(([kind]) => entries.some((entry) => entry.kind === kind))
            .map(([kind, style]) => (
              <span
                key={kind}
                className="flex items-center gap-1.5 font-avenir text-xs text-padeSubtext"
              >
                <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                {style.label}
              </span>
            ))}
        </div>
      )}
    </div>
  )
}

const PersonChip: React.FC<{
  entry: ILeaveCalendarEntry
  showLeaveType?: boolean
  dimmed?: boolean
  onHover?: () => void
  onLeave?: () => void
  onSelect?: () => void
}> = ({ entry, showLeaveType, dimmed, onHover, onLeave, onSelect }) => {
  const style = KIND_STYLE[entry.kind] ?? KIND_STYLE.planned

  // A run of leave is one absence, so hovering any of its days highlights the rest of them.
  return (
    <Tooltip
      title={
        <span className="font-avenir text-xs">
          {entry.full_name}
          {showLeaveType && entry.leave_policy ? ` · ${entry.leave_policy}` : ""}
          <br />
          {entry.first_day} – {entry.last_day} · {style.label}
        </span>
      }
    >
      <div
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onSelect}
        onKeyDown={
          onSelect
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  onSelect()
                }
              }
            : undefined
        }
        role={onSelect ? "button" : undefined}
        tabIndex={onSelect ? 0 : undefined}
        className={`font-avenir text-[11px] leading-tight truncate rounded px-1.5 py-0.5 border transition-opacity ${
          onSelect ? "cursor-pointer hover:brightness-95" : "cursor-default"
        } ${style.chip} ${dimmed ? "opacity-40" : "opacity-100"}`}
      >
        {entry.full_name ?? "Someone"}
      </div>
    </Tooltip>
  )
}

export default LeaveMonthGrid
